import Tooltip from 'antd/es/tooltip';
import Col from 'antd/lib/col';
import Divider from 'antd/lib/divider';
import Row from 'antd/lib/row';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import Empty from 'antd/lib/empty';
import { theme } from 'antd/lib';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, LegendScrollComponent, MarkAreaComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useMemo } from 'react';
import { IPriceResponseEventMetrics } from '../../domain/price-response/interface';
import { calculateTotalEventsMetrics } from '../../domain/price-response/priceResponseMetricCalculation';
import { ContentBlock } from '../ContentBlock/ContentBlock';
import { ReactComponent as ClockIcon } from '../icons/clock.svg';
import { DEFAULT_DATE_FORMAT, getTimezoneFormattedTime } from '../../domain/date/date';
import { PriceResponseMarket } from '../../pages/priceResponse/view/scenarios/PriceResponseMarket';
import { EChartsOption } from 'echarts';

const CHART_GROUP_ID = 'price-response-analytic-charts';

echarts.connect(CHART_GROUP_ID);

echarts.use([BarChart, TooltipComponent, GridComponent, CanvasRenderer, MarkAreaComponent, DataZoomComponent, LegendScrollComponent]);

interface IEventStatisticWidget {
    events: IPriceResponseEventMetrics[];
    market: PriceResponseMarket;
    start?: Date;
    end?: Date;
}

const hourFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 2,
});

export const EventStatisticWidget = ({ events, market, start, end }: IEventStatisticWidget) => {
    const { token: themeColors } = theme.useToken();
    const eventsTotalMetrics = useMemo(() => calculateTotalEventsMetrics(events), [events]);

    // No separate transform needed - sort and map straight to [timestamp, count] pairs
    // for a time-based xAxis instead of splitting into parallel arrays.
    const seriesData = useMemo(
        () =>
            [...events]
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                .map(({ date, count, hours }) => [new Date(date).getTime(), count, hours]),
        [events]
    );

    const isNoData = seriesData.every(([, count]) => count === 0);

    const option: EChartsOption = {
        grid: {
            top: '40px',
            left: '80px',
            right: '40px',
            bottom: '60px',
        },
        tooltip: {
            trigger: 'axis',
            padding: 0,
            borderColor: '#ffffff',
            axisPointer: {
                type: 'line',
                lineStyle: { type: 'dashed' },
            },
            formatter: (params: any) => {
                const point = Array.isArray(params) ? params[0] : params;
                const [timestamp, count, hours] = point.value as [number, number, number];

                const dateString = getTimezoneFormattedTime(new Date(timestamp), market.timezone, DEFAULT_DATE_FORMAT);

                return `<div style="padding: 6px 10px;">
                    <div style="color: #697475; margin-bottom: 4px;">${dateString}</div>
                    <div>${point.marker} Events: <strong>${count}</strong></div>
                    <div>Event hours: <strong>${hourFormatter.format(hours)}</strong></div>
                </div>`;
            },
        },
        xAxis: {
            type: 'time',
            axisTick: { show: false },
            min: start && start.getTime(),
            max: end && end.getTime(),
            axisLabel: {
                show: false,
            },
        },
        yAxis: {
            type: 'value',
            nameLocation: 'middle',
            splitLine: {
                show: true,
            },
        },
        series: [
            {
                type: 'bar',
                barMaxWidth: '30px',
                data: seriesData,
                itemStyle: {
                    color: themeColors.colorPrimary,
                },
                emphasis: { disabled: true },
                label: {
                    show: true,
                    position: 'bottom',
                    distance: 12,
                    color: '#697475',
                    formatter: params => {
                        const timestamp = (params.value as [number, number, number])[0];

                        return getTimezoneFormattedTime(new Date(timestamp), market.timezone, DEFAULT_DATE_FORMAT);
                    },
                },
                labelLayout: {
                    hideOverlap: true,
                },
            },
        ],
        dataZoom: [
            {
                type: 'inside',
            },
            {
                type: 'slider',
                height: 20,
                bottom: 10,
                labelFormatter: value => getTimezoneFormattedTime(new Date(value), market.timezone),
            },
        ],
    };

    return (
        <Row
            justify="space-between"
            gutter={[0, 0]}
            className="content-background"
            style={{ marginTop: '16px', marginBottom: '16px', padding: '32px 8px', borderRadius: 8 }}
        >
            <Col xs={24} md={11}>
                <div className="content-row">
                    <Row align="middle" justify="center">
                        <Tooltip title="General statistics about Price Response events in the selected period">
                            <Space align="center">
                                <ClockIcon style={{ width: '40px', height: '40px', fill: themeColors.colorPrimary }} />
                                <Typography.Title level={2} style={{ color: themeColors.colorPrimary, margin: 0 }}>
                                    Events
                                </Typography.Title>
                            </Space>
                        </Tooltip>
                    </Row>
                    <ContentBlock
                        value={eventsTotalMetrics.days}
                        description={
                            <>
                                Total number <br /> of Event days
                            </>
                        }
                        descriptionClass="main-content-description"
                        valueClass="main-content-value"
                    />
                </div>
            </Col>
            <Col xs={0} md={2} style={{ display: 'flex', justifyContent: 'center' }}>
                <Divider type="vertical" style={{ height: '100%' }} />
            </Col>
            <Col xs={24} md={0}>
                <Divider style={{ height: '100%' }} />
            </Col>
            <Col xs={24} md={11}>
                <div className="content-row">
                    <ContentBlock
                        value={eventsTotalMetrics.count}
                        description={
                            <>
                                Total number <br />
                                of Site Events
                            </>
                        }
                        descriptionClass="content-description"
                        valueClass="content-value"
                    />
                    <ContentBlock
                        value={hourFormatter.format(eventsTotalMetrics.hours)}
                        description={
                            <>
                                Total number <br />
                                of Event Hours
                            </>
                        }
                        descriptionClass="content-description"
                        valueClass="content-value"
                    />
                </div>
            </Col>
            <Col span={24}>
                {isNoData ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <ReactEChartsCore
                        echarts={echarts}
                        option={option}
                        onChartReady={instance => {
                            instance.group = CHART_GROUP_ID;
                        }}
                        notMerge
                        lazyUpdate
                    />
                )}
            </Col>
        </Row>
    );
};
